/**
 * TanStack Query hooks for study protocol operations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import type { ProtocolData, StudyProtocol } from "@/generated/sdk"
import { StudyProtocolStatusEnum } from "@/generated/sdk"
import { studyProtocolApiService } from "@/services/studyProtocolApi"

export const protocolKeys = {
  all: ["protocol"] as const,
  details: () => [...protocolKeys.all, "detail"] as const,
  detail: (studyId: string | undefined) => [...protocolKeys.details(), studyId ?? null] as const,
}

/**
 * Fetches protocol for a study. Polls every 5s when status is IN_PROGRESS or NOT_STARTED.
 */
export function useProtocolDetail(studyId: string | undefined) {
  return useQuery({
    queryKey: protocolKeys.detail(studyId),
    queryFn: () => studyProtocolApiService.getProtocol(studyId as string),
    enabled: !!studyId,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === StudyProtocolStatusEnum.InProgress ||
        status === StudyProtocolStatusEnum.NotStarted
        ? 5000
        : false
    },
  })
}

/**
 * Creates (POST) or updates+submits (PUT) a study protocol, then refreshes the cached protocol
 * detail so the study's protocol view reflects the saved data.
 */
export function useSaveProtocol(studyId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ data, isNew }: { data: ProtocolData; isNew: boolean }) =>
      isNew
        ? studyProtocolApiService.createProtocol(studyId, data)
        : studyProtocolApiService.updateProtocol(studyId, data, true),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: protocolKeys.detail(studyId) })
    },
  })
}

/**
 * Uploads a protocol file. Optimistically transitions to IN_PROGRESS on mutate so the UI shows the
 * extracting state immediately (clearing any stale error). Polling picks up the real server state
 * after that.
 */
export function useUploadProtocol(studyId: string) {
  const queryClient = useQueryClient()
  const key = protocolKeys.detail(studyId)

  return useMutation({
    mutationFn: (file: File) => studyProtocolApiService.uploadProtocol(studyId, file),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: key })
      const previous = queryClient.getQueryData<StudyProtocol | null>(key)
      queryClient.setQueryData<StudyProtocol | null>(key, (old) =>
        old
          ? { ...old, status: StudyProtocolStatusEnum.InProgress, error: undefined }
          : ({ status: StudyProtocolStatusEnum.InProgress } as StudyProtocol),
      )
      return { previous }
    },
    onSuccess: () => {
      queryClient.setQueryData<StudyProtocol | null>(key, (old) =>
        old
          ? { ...old, status: StudyProtocolStatusEnum.InProgress, error: undefined }
          : ({ status: StudyProtocolStatusEnum.InProgress } as StudyProtocol),
      )
    },
    onError: (_err, _file, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData<StudyProtocol | null>(key, context.previous)
      }
    },
  })
}
